import React from "react";
import { ArrowRightLeft, Coins, Download, IndianRupee, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatINR } from "@/lib/api";
import { grams, goldBalanceClass, inrBalanceClass } from "@/pages/admin/suppliers/supplierHelpers";
import { StatementTable } from "@/pages/admin/suppliers/StatementTable";

// Dual-currency ledger dialog (gold + rupee tabs), the fixed-rate Settle/Convert widget,
// and the ledger PDF download — mirrors the Partner Ledger on the payable side.
export const SupplierLedgerDialog = ({
  open, onClose, supplier, ledger, ledgerLoading, goldRate, downloading, onDownload,
  convDir, convAmount, setConvAmount, convPreview, converting, onStartConvert, onCancelConvert, onSubmitConvert,
  onOpenAdjustment,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent data-testid="supplier-ledger-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="flex items-center justify-between pr-6 flex-wrap gap-2">
        <span>Supplier Ledger — {supplier?.name}</span>
        <Button size="sm" variant="outline" className="gap-1" onClick={onOpenAdjustment}>+/− Adjustment</Button>
      </DialogTitle></DialogHeader>
      {ledgerLoading || !ledger ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Loading...</p>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-[#FBF6EE] p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium"><ArrowRightLeft className="h-4 w-4 text-[hsl(var(--primary))]" /> Settle / Convert</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Today&apos;s 24K rate: <span className="font-mono text-foreground">{goldRate ? formatINR(goldRate) : "—"}/g</span></span>
                <Button data-testid="supplier-pdf-in-dialog" variant="outline" size="sm" className="gap-1.5" disabled={downloading} onClick={() => onDownload(supplier)}>
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                </Button>
              </div>
            </div>
            {!convDir ? (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button data-testid="settle-cash-in-gold" size="sm" variant="secondary" className="gap-1.5" disabled={!goldRate} onClick={() => onStartConvert("rupee_to_gold")}>
                  <IndianRupee className="h-3.5 w-3.5" /> Settle Cash in Gold
                </Button>
                <Button data-testid="settle-gold-in-cash" size="sm" variant="secondary" className="gap-1.5" disabled={!goldRate} onClick={() => onStartConvert("gold_to_rupee")}>
                  <Coins className="h-3.5 w-3.5" /> Settle Gold in Cash
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <Label className="text-xs">{convDir === "rupee_to_gold" ? "Rupee amount (₹)" : "Gold weight (fine g)"}</Label>
                  <Input data-testid="convert-amount-input" type="number" value={convAmount} onChange={(e) => setConvAmount(e.target.value)} className="mt-1 font-mono" placeholder={convDir === "rupee_to_gold" ? "0" : "0.000"} />
                </div>
                <div className="text-sm pb-2"><span className="text-muted-foreground">→ </span><span data-testid="convert-preview" className="font-mono font-semibold text-[hsl(var(--gold-dark))]">{convPreview || "—"}</span></div>
                <div className="flex gap-2 pb-1">
                  <Button data-testid="convert-cancel" size="sm" variant="ghost" onClick={onCancelConvert}>Cancel</Button>
                  <Button data-testid="convert-submit" size="sm" disabled={converting || !convAmount} onClick={onSubmitConvert}>{converting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Convert</Button>
                </div>
              </div>
            )}
          </div>

          <Tabs defaultValue="gold" className="mt-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <TabsList>
                <TabsTrigger data-testid="supplier-statement-tab-gold" value="gold" className="gap-1.5"><Coins className="h-3.5 w-3.5" /> Gold</TabsTrigger>
                <TabsTrigger data-testid="supplier-statement-tab-inr" value="inr" className="gap-1.5"><IndianRupee className="h-3.5 w-3.5" /> Rupee</TabsTrigger>
              </TabsList>
              <div className="flex gap-2">
                <Badge className={`bg-[hsl(var(--gold-light))] border border-border ${goldBalanceClass(ledger.gold_balance_fine_g)}`}>Gold: {grams(ledger.gold_balance_fine_g)}</Badge>
                <Badge className={`bg-[#F7F0E6] border border-border ${inrBalanceClass(ledger.inr_balance)}`}>Rupee: {formatINR(ledger.inr_balance)}</Badge>
              </div>
            </div>
            <TabsContent value="gold" className="mt-4 max-h-[45vh] overflow-y-auto">
              <StatementTable entries={ledger.gold_entries} unit="gold" />
              <div className="flex justify-end gap-6 text-sm mt-3 pr-2">
                <span className="text-muted-foreground">Debited <span className="font-mono text-foreground">{grams(ledger.gold_total_debit)}</span></span>
                <span className="text-muted-foreground">Credited <span className="font-mono text-foreground">{grams(ledger.gold_total_credit)}</span></span>
                <span className="font-medium">Balance <span className={`font-mono ${goldBalanceClass(ledger.gold_balance_fine_g)}`}>{grams(ledger.gold_balance_fine_g)}</span></span>
              </div>
            </TabsContent>
            <TabsContent value="inr" className="mt-4 max-h-[45vh] overflow-y-auto">
              <StatementTable entries={ledger.inr_entries} unit="inr" />
              <div className="flex justify-end gap-6 text-sm mt-3 pr-2">
                <span className="text-muted-foreground">Debited <span className="font-mono text-foreground">{formatINR(ledger.inr_total_debit)}</span></span>
                <span className="text-muted-foreground">Credited <span className="font-mono text-foreground">{formatINR(ledger.inr_total_credit)}</span></span>
                <span className="font-medium">Balance <span className={`font-mono ${inrBalanceClass(ledger.inr_balance)}`}>{formatINR(ledger.inr_balance)}</span></span>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </DialogContent>
  </Dialog>
);
